import React, { FC, useState } from 'react';
import { useTranslate } from 'react-admin';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import cx from 'classnames';

import MuiTableCell from '~/components/TableCell';
import { H2, JsonField, ReferenceField, TextField } from '~/components';
import { Section } from '~/layout';
import { ApprovalCaseDetail } from '~/types/approvalCase';
import Grid from '@material-ui/core/Grid';
import longdash from '~/utils/Longdash';

type DiffType = 'unchanged' | 'changed' | 'added' | 'removed';

interface DiffRow {
  key: string;
  currentValue: string | null;
  newValue: string | null;
  diffType: DiffType;
}

const flattenJson = (obj: any, prefix = ''): Record<string, string | null> => {
  const result: Record<string, string | null> = {};
  if (obj == null || typeof obj !== 'object') return result;
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (Array.isArray(v)) {
      v.forEach((item, i) => {
        const aKey = `${key}[${i + 1}]`;
        if (item != null && typeof item === 'object') {
          Object.assign(result, flattenJson(item, aKey));
        } else {
          result[aKey] = item == null ? null : String(item);
        }
      });
    } else if (v != null && typeof v === 'object') {
      Object.assign(result, flattenJson(v, key));
    } else {
      result[key] = v == null ? null : String(v);
    }
  }
  return result;
};

const buildDiff = (current: Record<string, any>, next: Record<string, any>): DiffRow[] => {
  const flatCurrent = flattenJson(current);
  const flatNext = flattenJson(next);
  const allKeys = Array.from(
    new Set([...Object.keys(flatCurrent), ...Object.keys(flatNext)])
  ).sort();

  return allKeys.map((key) => {
    const hasCur = Object.prototype.hasOwnProperty.call(flatCurrent, key);
    const hasNew = Object.prototype.hasOwnProperty.call(flatNext, key);
    const curVal = hasCur ? flatCurrent[key] : null;
    const newVal = hasNew ? flatNext[key] : null;

    let diffType: DiffType;
    if (!hasCur) diffType = 'added';
    else if (!hasNew) diffType = 'removed';
    else if (curVal !== newVal) diffType = 'changed';
    else diffType = 'unchanged';

    return { key, currentValue: curVal, newValue: newVal, diffType };
  });
};

const displayValue = (v: string | null) => (v == null || v === '' ? longdash : v);

const DataTab: FC<any> = (props) => {
  const { record } = props;
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.approvals.show.data.${key}`);
  const classes = useStyles();
  const [onlyChanges, setOnlyChanges] = useState(false);

  const approvalCase = record as ApprovalCaseDetail | undefined;
  const isUpdate = approvalCase?.actionType === 'UPDATE' && !!approvalCase?.objectId;

  if (!approvalCase) return null;

  if (isUpdate) {
    const rows: DiffRow[] = buildDiff(approvalCase.originalBody ?? {}, approvalCase.actionBody);

    const visibleRows = onlyChanges ? rows.filter((r) => r.diffType !== 'unchanged') : rows;

    return (
      <div>
        <H2 topSpacing={0} bottomSpacing={2}>
          {t('objectDetails')}
        </H2>
        <Section>
          <Grid container spacing={2} className={classes.bottomSpacing}>
            <Grid item xs={4}>
              <ReferenceField
                record={{ id: approvalCase.objectId, type: approvalCase.objectType }}
              />
            </Grid>
          </Grid>
        </Section>

        <Box className={classes.header}>
          <H2 topSpacing={1} bottomSpacing={2}>
            {t('objectData')}
          </H2>
          <Button size='small' color='primary' onClick={() => setOnlyChanges((v) => !v)}>
            {onlyChanges ? t('showAll') : t('showOnlyChanges')}
          </Button>
        </Box>
        <DiffDataTable rows={visibleRows} />
      </div>
    );
  }

  if (approvalCase?.actionType === 'CREATE') {
    return (
      <div>
        <H2 topSpacing={0} bottomSpacing={2}>
          {t('objectData')}
        </H2>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <MuiTableCell className={classes.fieldCol}>{t('field')}</MuiTableCell>
              <MuiTableCell>{t('newValue')}</MuiTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(flattenJson(approvalCase.actionBody)).map(([key, value]) => {
              return (
                <TableRow key={key}>
                  <MuiTableCell className={cx(classes.fieldCol)}>{key}</MuiTableCell>
                  <MuiTableCell>
                    <TextField textWrap>{displayValue(value)}</TextField>
                  </MuiTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (approvalCase?.actionType === 'ATOMIC') {
    return (
      <div>
        <H2 topSpacing={0} bottomSpacing={2}>
          {t('objectDetails')}
        </H2>
        <Section>
          <Grid container spacing={2} className={classes.bottomSpacing}>
            <Grid item xs={4}>
              <ReferenceField
                record={{ id: approvalCase.objectId, type: approvalCase.objectType }}
              />
            </Grid>
          </Grid>
        </Section>

        <H2 topSpacing={1} bottomSpacing={2}>
          {t('actionDetails')}
        </H2>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <MuiTableCell className={classes.fieldCol}>{t('parameter')}</MuiTableCell>
              <MuiTableCell>{t('value')}</MuiTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(flattenJson(approvalCase.actionBody)).map(([key, value]) => {
              return (
                <TableRow key={key}>
                  <MuiTableCell className={cx(classes.fieldCol)}>{key}</MuiTableCell>
                  <MuiTableCell>
                    <TextField textWrap>{displayValue(value)}</TextField>
                  </MuiTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <JsonField
      json={approvalCase.actionBody}
      reactJsonOptions={{
        name: null,
        collapsed: false,
        enableClipboard: false,
        displayDataTypes: false,
        style: { wordBreak: 'break-all' },
      }}
    />
  );
};

interface DiffDataTableRows {
  isLoading?: boolean;
  rows: DiffRow[];
}

const DiffDataTable: FC<DiffDataTableRows> = (props) => {
  const { rows, isLoading } = props;
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.approvals.show.data.${key}`);

  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          <MuiTableCell className={classes.fieldCol}>{t('field')}</MuiTableCell>
          <MuiTableCell>{t('currentValue')}</MuiTableCell>
          <MuiTableCell>{t('newValue')}</MuiTableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <MuiTableCell loading />
                <MuiTableCell loading />
                <MuiTableCell loading />
              </TableRow>
            ))
          : rows.map((row) => {
              const colorClass = classes[row.diffType];
              return (
                <TableRow key={row.key}>
                  <MuiTableCell className={cx(classes.fieldCol, colorClass)}>
                    {row.key}
                  </MuiTableCell>
                  <MuiTableCell className={colorClass}>
                    <TextField textWrap>{displayValue(row.currentValue)}</TextField>
                  </MuiTableCell>
                  <MuiTableCell className={colorClass}>
                    <TextField textWrap>{displayValue(row.newValue)}</TextField>
                  </MuiTableCell>
                </TableRow>
              );
            })}
      </TableBody>
    </Table>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  table: {
    tableLayout: 'fixed',
    width: '100%',
  },
  fieldCol: {
    width: '30%',
  },
  bottomSpacing: {
    paddingBottom: theme.spacing(3),
  },
  unchanged: {},
  changed: { color: theme.palette.warning.main },
  added: { color: theme.palette.success.main },
  removed: { color: theme.palette.error.main },
}));

export default DataTab;
